import { 
  collection, doc, getDoc, setDoc, updateDoc, query, where, getDocs, addDoc, deleteDoc,
  writeBatch, serverTimestamp, Timestamp, onSnapshot, orderBy, limit as firestoreLimit
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Database Collections
export const IDENTITY_COLLECTIONS = {
  IDENTITY_MAPPING: 'user_identity_mapping',
  BANNED_EMAILS: 'banned_emails',
  ANONYMOUS_AUDIT_LOG: 'anonymous_audit_log'
} as const;

// Types
export interface UserIdentityMapping {
  id: string; // Document ID = anonymous identifier
  email: string; // Real email for ban enforcement
  uid: string; // Firebase Auth UID
  createdAt: Timestamp;
  updatedAt: Timestamp;
  isActive: boolean;
  previousEmails: string[]; // Track email changes
  previousAnonymousIds: string[]; // Track ID changes (admin-only)
  metadata: {
    registrationSource: string;
    lastKnownIP?: string;
    deviceFingerprint?: string;
  };
}

export interface BannedEmail {
  id: string; // Document ID = email hash
  email: string;
  bannedAt: Timestamp;
  bannedBy: string; // Admin ID
  reason: string;
  associatedAnonymousIds: string[];
  isPermanent: boolean;
  expiresAt?: Timestamp;
}

export interface AnonymousAuditLog {
  id: string;
  adminId: string;
  action: 'identity_lookup' | 'id_generation' | 'id_change' | 'ban_enforcement';
  anonymousId: string;
  email?: string; // Only for lookups
  timestamp: Timestamp;
  reason: string;
  ipAddress: string;
}

// Anonymous ID Generation Service
class AnonymousIdentityService {
  private static instance: AnonymousIdentityService;
  
  constructor() {
    if (AnonymousIdentityService.instance) {
      return AnonymousIdentityService.instance;
    }
    AnonymousIdentityService.instance = this;
  }

  /**
   * Generates a cryptographically secure anonymous identifier
   */
  generateAnonymousId(): string {
    // Generate random bytes using Web Crypto API or fallback
    const array = new Uint8Array(16); // 128 bits
    
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      window.crypto.getRandomValues(array);
    } else if (typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.getRandomValues) {
      globalThis.crypto.getRandomValues(array);
    } else {
      // Fallback for older environments
      for (let i = 0; i < array.length; i++) {
        array[i] = Math.floor(Math.random() * 256);
      }
    }
    
    // Convert to base36 string
    let result = '';
    for (let i = 0; i < array.length; i++) {
      result += array[i].toString(36).padStart(2, '0');
    }
    
    // Take first 12 characters and add prefix
    return `anon_${result.substring(0, 12)}`;
  }

  /**
   * Creates a new identity mapping for a user
   */
  async createIdentityMapping(
    uid: string, 
    email: string, 
    metadata: Partial<UserIdentityMapping['metadata']> = {}
  ): Promise<string> {
    try {
      // Check if user already has an identity mapping
      const existingMapping = await this.getIdentityByUid(uid);
      if (existingMapping) {
        return existingMapping.id;
      }

      // Check if email is banned
      const isBanned = await this.isEmailBanned(email);
      if (isBanned) {
        throw new Error('Email address is banned from the platform');
      }

      // Generate unique anonymous ID
      let anonymousId = this.generateAnonymousId();
      let isUnique = false;
      let attempts = 0;
      
      while (!isUnique && attempts < 10) {
        const existingDoc = await getDoc(doc(db, IDENTITY_COLLECTIONS.IDENTITY_MAPPING, anonymousId));
        if (!existingDoc.exists()) {
          isUnique = true;
        } else {
          anonymousId = this.generateAnonymousId();
          attempts++;
        }
      }

      if (!isUnique) {
        throw new Error('Unable to generate unique anonymous identifier');
      }

      // Create identity mapping
      const identityMapping: UserIdentityMapping = {
        id: anonymousId,
        email: email.toLowerCase(),
        uid,
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
        isActive: true,
        previousEmails: [],
        previousAnonymousIds: [],
        metadata: {
          registrationSource: 'auth_registration',
          ...metadata
        }
      };

      await setDoc(doc(db, IDENTITY_COLLECTIONS.IDENTITY_MAPPING, anonymousId), identityMapping);
      
      // Log the creation
      await this.createAuditLog('admin_system', 'id_generation', anonymousId, undefined, 'New user registration');

      return anonymousId;
    } catch (error) {
      console.error('Error creating identity mapping:', error);
      throw error;
    }
  }

  /**
   * Resolves an anonymous ID to email (admin-only function)
   */
  async resolveAnonymousId(anonymousId: string, adminId: string, reason: string): Promise<string | null> {
    try {
      const mappingDoc = await getDoc(doc(db, IDENTITY_COLLECTIONS.IDENTITY_MAPPING, anonymousId));
      
      if (!mappingDoc.exists()) {
        return null;
      }

      const mapping = mappingDoc.data() as UserIdentityMapping;
      
      // Log the lookup for audit purposes
      await this.createAuditLog(adminId, 'identity_lookup', anonymousId, mapping.email, reason);

      return mapping.email;
    } catch (error) {
      console.error('Error resolving anonymous ID:', error);
      throw error;
    }
  }

  /**
   * Gets identity mapping by Firebase UID
   */
  async getIdentityByUid(uid: string): Promise<UserIdentityMapping | null> {
    try {
      const q = query(
        collection(db, IDENTITY_COLLECTIONS.IDENTITY_MAPPING),
        where('uid', '==', uid),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      return querySnapshot.docs[0].data() as UserIdentityMapping;
    } catch (error) {
      console.error('Error getting identity by UID:', error);
      throw error;
    }
  }

  /**
   * Gets identity mapping by email (admin-only)
   */
  async getIdentityByEmail(email: string): Promise<UserIdentityMapping | null> {
    try {
      const q = query(
        collection(db, IDENTITY_COLLECTIONS.IDENTITY_MAPPING),
        where('email', '==', email.toLowerCase()),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      return querySnapshot.docs[0].data() as UserIdentityMapping;
    } catch (error) {
      console.error('Error getting identity by email:', error);
      throw error;
    }
  }

  /**
   * Gets all identity mappings for an email (for ban system)
   */
  async getIdentitiesByEmail(email: string): Promise<UserIdentityMapping[]> {
    try {
      const q = query(
        collection(db, IDENTITY_COLLECTIONS.IDENTITY_MAPPING),
        where('email', '==', email.toLowerCase()),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return [];
      }

      return querySnapshot.docs.map(doc => doc.data() as UserIdentityMapping);
    } catch (error) {
      console.error('Error getting identities by email:', error);
      throw error;
    }
  }

  /**
   * Gets identity mapping by anonymous ID
   */
  async getIdentityByAnonymousId(anonymousId: string): Promise<UserIdentityMapping | null> {
    try {
      const q = query(
        collection(db, IDENTITY_COLLECTIONS.IDENTITY_MAPPING),
        where('anonymousId', '==', anonymousId),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      return querySnapshot.docs[0].data() as UserIdentityMapping;
    } catch (error) {
      console.error('Error getting identity by anonymous ID:', error);
      throw error;
    }
  }

  /**
   * Updates user email while maintaining ban tracking
   */
  async updateUserEmail(
    anonymousId: string, 
    oldEmail: string, 
    newEmail: string, 
    adminId?: string
  ): Promise<void> {
    try {
      // Check if new email is banned
      const isBanned = await this.isEmailBanned(newEmail);
      if (isBanned) {
        throw new Error('New email address is banned from the platform');
      }

      const mappingRef = doc(db, IDENTITY_COLLECTIONS.IDENTITY_MAPPING, anonymousId);
      const mappingDoc = await getDoc(mappingRef);
      
      if (!mappingDoc.exists()) {
        throw new Error('Identity mapping not found');
      }

      const mapping = mappingDoc.data() as UserIdentityMapping;
      
      // Update the mapping
      await updateDoc(mappingRef, {
        email: newEmail.toLowerCase(),
        previousEmails: [...mapping.previousEmails, oldEmail.toLowerCase()],
        updatedAt: serverTimestamp()
      });

      // Log the change
      const reason = `Email change from ${oldEmail} to ${newEmail}`;
      await this.createAuditLog(adminId || 'system', 'identity_lookup', anonymousId, newEmail, reason);
    } catch (error) {
      console.error('Error updating user email:', error);
      throw error;
    }
  }

  /**
   * Bans an email address and all associated anonymous IDs
   */
  async banEmailAddress(
    email: string, 
    adminId: string, 
    reason: string, 
    isPermanent: boolean = true,
    expiresAt?: Date
  ): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      // Get all identity mappings for this email (including historical)
      const currentMapping = await this.getIdentityByEmail(email);
      const allAssociatedIds: string[] = [];
      
      if (currentMapping) {
        allAssociatedIds.push(currentMapping.id);
        
        // Also find any mappings that have this email in their previousEmails
        const historicalQuery = query(
          collection(db, IDENTITY_COLLECTIONS.IDENTITY_MAPPING),
          where('previousEmails', 'array-contains', email.toLowerCase())
        );
        
        const historicalSnapshot = await getDocs(historicalQuery);
        historicalSnapshot.docs.forEach(doc => {
          if (!allAssociatedIds.includes(doc.id)) {
            allAssociatedIds.push(doc.id);
          }
        });
      }

      // Create banned email record
      const emailHash = await this.hashEmail(email.toLowerCase());
      const bannedEmail: BannedEmail = {
        id: emailHash,
        email: email.toLowerCase(),
        bannedAt: serverTimestamp() as Timestamp,
        bannedBy: adminId,
        reason,
        associatedAnonymousIds: allAssociatedIds,
        isPermanent,
        expiresAt: expiresAt ? Timestamp.fromDate(expiresAt) : undefined
      };

      batch.set(doc(db, IDENTITY_COLLECTIONS.BANNED_EMAILS, emailHash), bannedEmail);

      // Deactivate all associated identity mappings
      for (const anonymousId of allAssociatedIds) {
        const mappingRef = doc(db, IDENTITY_COLLECTIONS.IDENTITY_MAPPING, anonymousId);
        batch.update(mappingRef, {
          isActive: false,
          updatedAt: serverTimestamp()
        });
      }

      await batch.commit();

      // Log the ban
      await this.createAuditLog(adminId, 'ban_enforcement', allAssociatedIds[0] || 'unknown', email, reason);
    } catch (error) {
      console.error('Error banning email address:', error);
      throw error;
    }
  }

  /**
   * Checks if an email address is banned
   */
  async isEmailBanned(email: string): Promise<boolean> {
    try {
      const emailHash = await this.hashEmail(email.toLowerCase());
      const bannedDoc = await getDoc(doc(db, IDENTITY_COLLECTIONS.BANNED_EMAILS, emailHash));
      
      if (!bannedDoc.exists()) {
        return false;
      }

      const bannedEmail = bannedDoc.data() as BannedEmail;
      
      // Check if ban has expired
      if (!bannedEmail.isPermanent && bannedEmail.expiresAt) {
        const now = new Date();
        const expirationDate = bannedEmail.expiresAt.toDate();
        
        if (now > expirationDate) {
          // Ban has expired, remove it
          await deleteDoc(doc(db, IDENTITY_COLLECTIONS.BANNED_EMAILS, emailHash));
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Error checking if email is banned:', error);
      return false; // Fail open for user experience
    }
  }

  /**
   * Generates a new anonymous ID for an existing user (admin-only)
   */
  async regenerateAnonymousId(
    currentAnonymousId: string, 
    adminId: string, 
    reason: string
  ): Promise<string> {
    try {
      const mappingRef = doc(db, IDENTITY_COLLECTIONS.IDENTITY_MAPPING, currentAnonymousId);
      const mappingDoc = await getDoc(mappingRef);
      
      if (!mappingDoc.exists()) {
        throw new Error('Current identity mapping not found');
      }

      const currentMapping = mappingDoc.data() as UserIdentityMapping;
      
      // Generate new anonymous ID
      let newAnonymousId = this.generateAnonymousId();
      let isUnique = false;
      let attempts = 0;
      
      while (!isUnique && attempts < 10) {
        const existingDoc = await getDoc(doc(db, IDENTITY_COLLECTIONS.IDENTITY_MAPPING, newAnonymousId));
        if (!existingDoc.exists()) {
          isUnique = true;
        } else {
          newAnonymousId = this.generateAnonymousId();
          attempts++;
        }
      }

      if (!isUnique) {
        throw new Error('Unable to generate unique anonymous identifier');
      }

      // Create new mapping and deactivate old one
      const batch = writeBatch(db);
      
      // Deactivate old mapping
      batch.update(mappingRef, {
        isActive: false,
        updatedAt: serverTimestamp()
      });
      
      // Create new mapping
      const newMapping: UserIdentityMapping = {
        ...currentMapping,
        id: newAnonymousId,
        previousAnonymousIds: [...currentMapping.previousAnonymousIds, currentAnonymousId],
        updatedAt: serverTimestamp() as Timestamp
      };
      
      batch.set(doc(db, IDENTITY_COLLECTIONS.IDENTITY_MAPPING, newAnonymousId), newMapping);
      
      await batch.commit();
      
      // Log the change
      await this.createAuditLog(adminId, 'id_change', newAnonymousId, currentMapping.email, reason);

      return newAnonymousId;
    } catch (error) {
      console.error('Error regenerating anonymous ID:', error);
      throw error;
    }
  }

  /**
   * Helper method to hash email addresses
   */
  private async hashEmail(email: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(email);
    
    if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } else if (typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.subtle) {
      const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } else {
      // Fallback: simple hash (not cryptographically secure)
      let hash = 0;
      for (let i = 0; i < email.length; i++) {
        const char = email.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      return Math.abs(hash).toString(16);
    }
  }

  /**
   * Creates an audit log entry
   */
  private async createAuditLog(
    adminId: string,
    action: AnonymousAuditLog['action'],
    anonymousId: string,
    email?: string,
    reason: string = '',
    ipAddress: string = 'unknown'
  ): Promise<void> {
    try {
      const auditLog: Omit<AnonymousAuditLog, 'id'> = {
        adminId,
        action,
        anonymousId,
        timestamp: serverTimestamp() as Timestamp,
        reason,
        ipAddress
      };

      // Only add email field if it's defined
      if (email) {
        auditLog.email = email;
      }

      await addDoc(collection(db, IDENTITY_COLLECTIONS.ANONYMOUS_AUDIT_LOG), auditLog);
    } catch (error) {
      console.error('Error creating audit log:', error);
      // Don't throw here to avoid breaking main operations
    }
  }

  /**
   * Gets audit logs for admin review (paginated)
   */
  async getAuditLogs(
    adminId?: string,
    limit: number = 100,
    startAfter?: Timestamp
  ): Promise<AnonymousAuditLog[]> {
    try {
      let q = query(
        collection(db, IDENTITY_COLLECTIONS.ANONYMOUS_AUDIT_LOG),
        orderBy('timestamp', 'desc'),
        firestoreLimit(limit)
      );

      if (adminId) {
        q = query(
          collection(db, IDENTITY_COLLECTIONS.ANONYMOUS_AUDIT_LOG),
          where('adminId', '==', adminId),
          orderBy('timestamp', 'desc'),
          firestoreLimit(limit)
        );
      }

      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as AnonymousAuditLog));
    } catch (error) {
      console.error('Error getting audit logs:', error);
      throw error;
    }
  }

  /**
   * Migrates existing users to anonymous ID system
   */
  async migrateExistingUsers(users: Array<{uid: string; email: string}>): Promise<{success: number; failed: string[]}> {
    let successCount = 0;
    const failedUsers: string[] = [];

    for (const user of users) {
      try {
        await this.createIdentityMapping(user.uid, user.email, {
          registrationSource: 'migration'
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to migrate user ${user.email}:`, error);
        failedUsers.push(user.email);
      }
    }

    return { success: successCount, failed: failedUsers };
  }

  /**
   * Real-time listener for identity changes (admin-only)
   */
  subscribeToIdentityChanges(
    callback: (mappings: UserIdentityMapping[]) => void,
    adminId: string
  ): () => void {
    const q = query(
      collection(db, IDENTITY_COLLECTIONS.IDENTITY_MAPPING),
      where('isActive', '==', true),
      orderBy('updatedAt', 'desc'),
      firestoreLimit(100)
    );

    return onSnapshot(q, (snapshot) => {
      const mappings = snapshot.docs.map(doc => doc.data() as UserIdentityMapping);
      callback(mappings);
    });
  }
}

// Export singleton instance
export const anonymousIdentityService = new AnonymousIdentityService();
export default anonymousIdentityService;